import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/ui/dialog';
import Ironview360PanoramaExperience from './panorama/Ironview360PanoramaExperience';
import {
  Tabs,
  LayoutsView,
  HotspotsView,
  MarkerContextMenu,
  ChangeSequenceDialog,
  AdjustSequenceWidget,
} from './components';
import { MarkerToolbar } from './layout/MarkerToolbar';
import { AreaFormDialog } from './components/AreaFormDialog';
import { PhotoSelectionTooltip } from './components/PhotoSelectionTooltip';
import { useInteractiveViewer } from './hooks/useInteractiveViewer';
import { useLayoutPdfInfo } from './hooks/useLayoutData';
import { Position } from './types/types';
import { cropPdfRegionToBlob, renderPdfPageToBlob } from './utils/cropPdfRegion';
import {
  clearViewerPanoramaDeepLinkParams,
  InteractiveViewerRoot,
  parseViewerDeepLink,
  resolveViewerDeepLinkLayoutId,
  shouldUsePdfRasterFallback,
  useInteractionHandlers,
  useViewerPdfDocument,
} from '@ironview/interactive-viewer';
import './styles/HotspotStyles.css';
import { BaseLayout } from '@/shared/components/layout';
import { PANORAMA_TABLET_DIALOG_CLOSE_CLASS } from './constants/constants';
// Importing for the side effect: ``registerTour`` is invoked at module load
// so the guided tours are available before we try to ``startTour`` below.
import { IN_PROGRESS_TOUR_ID } from '@/features/tours/in-progress';
import { ASBUILDS_TOUR_ID } from '@/features/tours/asbuilds';
import { useTour } from '@/features/tours';
import { normalizePdfRotation, toViewerLayout } from './adapters/ironview360Adapter';
import { getLayoutPdfRasterUrl } from './api/layout-pdf-api';

const InteractiveViewerRefactored: React.FC = () => {
  const { projectId } = useCurrentProject();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const lastAppliedDeepLinkRef = useRef<string>('');
  const [pendingHotspotId, setPendingHotspotId] = useState<number | null>(null);
  const [pdfPreviewImageUrl, setPdfPreviewImageUrl] = useState<string | null>(null);
  const pdfPreviewImageUrlRef = useRef<string | null>(null);

  // Detect layout type from current URL path
  const layoutType = React.useMemo(() => {
    const path = location.pathname;
    if (path.includes('/progress')) {
      return 'Progress';
    } else if (path.includes('/asbuilds')) {
      return 'asbuilds'; // This matches the backend logic for Asbuild + Corridor
    }
    return undefined;
  }, [location.pathname]);

  const { state, actions, refs, data, mutations } = useInteractiveViewer(layoutType);

  // Radix modal dialogs set `pointer-events: none` on `document.body` while
  // open. If that style sticks around after close/unmount, the sidebar becomes
  // unclickable even though it renders above the viewer.
  useEffect(() => {
    if (!state.showPhotoCarousel) {
      document.body.style.pointerEvents = '';
    }
  }, [state.showPhotoCarousel]);

  useEffect(() => {
    return () => {
      document.body.style.pointerEvents = '';
    };
  }, []);

  // Auto-start the appropriate guided tour the first time a user lands
  // on the route. Both Progress and Asbuilds use this same component but
  // hang off different feature flags + tour IDs.
  //
  // The provider hides repeat starts behind ``hasSeen`` internally, so
  // returning users won't see the tour again. We wait for ``hydrated``
  // so the persisted state has been read before deciding whether to show.
  //
  // Gating: we only auto-start when the project state can actually
  // demonstrate the feature — at least one layout, at least one photo
  // marker on the loaded layout, and that marker has at least one photo
  // attached. If those aren't true (fresh project, empty layouts, etc.)
  // we skip the tour entirely so the user doesn't see steps for things
  // that aren't on screen. The effect re-evaluates when ``photoMarkers``
  // resolves so the tour starts naturally once data finishes loading.
  const { hydrated: tourHydrated, active: activeTour, startTour } = useTour();
  const hasMarkerWithPhoto = useMemo(
    () => data.photoMarkers.some((m) => m.photos.length >= 1),
    [data.photoMarkers]
  );
  const tourIdForLayoutType = useMemo(() => {
    if (layoutType === 'Progress') return IN_PROGRESS_TOUR_ID;
    if (layoutType === 'asbuilds') return ASBUILDS_TOUR_ID;
    return null;
  }, [layoutType]);
  useEffect(() => {
    if (!tourIdForLayoutType) return;
    if (!tourHydrated) return;
    // Don't re-enter ``startTour`` while a tour is already running. Each
    // step transition triggers an optimistic cache update that re-
    // identifies ``startTour``; without this guard the effect re-fires
    // and snaps the user back to ``lastStepIndex`` mid-tour.
    if (activeTour) return;
    if (data.layouts.length < 1) return;
    if (!hasMarkerWithPhoto) return;
    void startTour(tourIdForLayoutType);
  }, [
    tourIdForLayoutType,
    tourHydrated,
    activeTour,
    startTour,
    data.layouts.length,
    hasMarkerWithPhoto,
  ]);

  // Fetch PDF metadata via the layout-scoped endpoint instead of the
  // drawings PDFPage endpoint. The drawings endpoint filters by the PDF
  // file's project, which 404s for non-staff users whenever a layout's
  // PDFPage references a PDFFile in a different project (e.g. layouts
  // attached via copy-from-existing). When that 404 happened the
  // rotation silently fell back to 0 and the interactive viewer
  // rendered the PDF unrotated — exactly the bug affected users hit.
  const layoutPdfInfoId = state.isPdfLayout ? (state.selectedLayoutId ?? null) : null;
  const { data: layoutPdfInfo } = useLayoutPdfInfo(layoutPdfInfoId);
  const selectedSourceLayout = useMemo(
    () =>
      data.layouts.find((layout) => String(layout.id) === String(state.selectedLayoutId)) ?? null,
    [data.layouts, state.selectedLayoutId]
  );
  const selectedViewerLayout = useMemo(() => {
    if (!selectedSourceLayout) return null;
    if (state.isPdfLayout && !layoutPdfInfo) return null;

    try {
      return toViewerLayout(selectedSourceLayout, {
        pdfInfo: layoutPdfInfo,
        imageDimensions: state.imageDimensions,
      });
    } catch (error) {
      console.error('Failed to normalize selected Interactive Viewer layout', error);
      return null;
    }
  }, [layoutPdfInfo, selectedSourceLayout, state.imageDimensions, state.isPdfLayout]);
  const selectedPdfAsset =
    selectedViewerLayout?.asset.kind === 'pdf' ? selectedViewerLayout.asset : null;
  const selectedTransformedAsset =
    selectedViewerLayout?.asset.kind === 'image' || selectedViewerLayout?.asset.kind === 'pdf'
      ? selectedViewerLayout.asset
      : null;
  const selectedLayoutPdfRotation =
    selectedPdfAsset?.rotation ?? normalizePdfRotation(layoutPdfInfo?.rotation);
  const pdfRasterFallback = useMemo(() => {
    if (!state.selectedLayoutId || !selectedPdfAsset?.width || !selectedPdfAsset.height) {
      return undefined;
    }
    return {
      url: getLayoutPdfRasterUrl(state.selectedLayoutId),
      width: selectedPdfAsset.width,
      height: selectedPdfAsset.height,
    };
  }, [selectedPdfAsset?.height, selectedPdfAsset?.width, state.selectedLayoutId]);
  const usePdfRasterFallback = Boolean(pdfRasterFallback && shouldUsePdfRasterFallback());

  const deepLinkParams = useMemo(() => parseViewerDeepLink(searchParams), [searchParams]);

  const addMeasurementPoint = useCallback(
    (point: Position) => {
      const adjustedPoint = { ...point };

      if (state.currentPolygon.length > 0) {
        const lastPoint = state.currentPolygon[state.currentPolygon.length - 1];

        if (state.isHorizontalConstrained) {
          adjustedPoint.y = lastPoint[1];
        } else if (state.isVerticalConstrained) {
          adjustedPoint.x = lastPoint[0];
        }
      }

      actions.setCurrentPolygon([...state.currentPolygon, [adjustedPoint.x, adjustedPoint.y]]);
    },
    [state.currentPolygon, state.isHorizontalConstrained, state.isVerticalConstrained, actions]
  );

  const interactionHandlers = useInteractionHandlers({
    mode: state.mode,
    photoIndexMode: state.photoIndexMode,
    isPdfLayout: state.isPdfLayout,
    isDragging: state.isDragging,
    scale: state.scale,
    position: state.position,
    displayPhotoMarkers: data.displayPhotoMarkers,
    svgRef: refs.svgRef,
    groupRef: refs.groupRef,
    dragStartRef: refs.dragStartRef,
    lastDragPointRef: refs.lastDragPointRef,
    dragStartTimeRef: refs.dragStartTimeRef,
    touchStartDistanceRef: refs.touchStartDistanceRef,
    touchStartScaleRef: refs.touchStartScaleRef,
    setIsDragging: (dragging) => actions.setIsDragging(dragging),
    setPosition: actions.setPosition,
    setScale: actions.setScale,
    setSelectedMarkers: actions.setSelectedMarkers,
    setLastSelectedMarkerId: actions.setLastSelectedMarkerId,
    setIsHorizontalConstrained: actions.setIsHorizontalConstrained,
    setIsVerticalConstrained: actions.setIsVerticalConstrained,
    addMeasurementPoint,
    addPhotoMarker: actions.addPhotoMarker,
    deleteSelectedMarkers: actions.deleteSelectedMarkers,
    copySelectedMarkers: actions.copySelectedMarkers,
    pasteMarkers: actions.pasteMarkers,
    undo: actions.undo,
    onUpdateMarkerAttributes: actions.setMarkerAttributes,
  });

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (state.photoSelectionTooltip.visible) {
        const target = e.target as HTMLElement;
        if (!target.closest('.photo-selection-tooltip')) {
          actions.setPhotoSelectionTooltip({ ...state.photoSelectionTooltip, visible: false });
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [state.photoSelectionTooltip.visible, actions]);

  const handlePhotoSelectionTooltipSelect = useCallback(
    (photoId: number, photoIndex: number) => {
      actions.setCurrentPhotoIndex(photoIndex);
      actions.setOpenedPhotoId(photoId);
      actions.showPanorama(photoId);
    },
    [actions]
  );

  const handlePhotoSelectionTooltipClose = useCallback(() => {
    actions.setPhotoSelectionTooltip({ ...state.photoSelectionTooltip, visible: false });
  }, [state.photoSelectionTooltip, actions]);

  const handlePanoramaOpenChange = useCallback(
    (open: boolean) => {
      actions.setShowPhotoCarousel(open);
      if (open) return;

      // Deep-link params are one-shot navigation context; clear them on close.
      const nextParams = clearViewerPanoramaDeepLinkParams(searchParams);
      if (nextParams.toString() === searchParams.toString()) return;
      const query = nextParams.toString();
      const nextUrl = `${location.pathname}${query ? `?${query}` : ''}${location.hash || ''}`;
      window.history.replaceState(null, '', nextUrl);
    },
    [actions, searchParams, location.pathname, location.hash]
  );

  // Subscribe to the same React-Query entry the shared PDF viewport
  // uses so the preview generator can reuse the already-parsed
  // PdfDocumentProxy instead of parsing the PDF a second time.
  //
  // IMPORTANT: only subscribe while the panorama dialog is actually
  // open. Otherwise, this hook would pin a second reference to the PDF
  // document between layout switches — preventing the React Query cache
  // entry for the previous PDF from being evicted (and therefore
  // destroyed). The end result was an OOM crash on the second layout
  // switch on mobile.
  const sharedPdfDocumentQuery = useViewerPdfDocument(
    state.showPhotoCarousel && state.isPdfLayout && state.pdfPath && !usePdfRasterFallback
      ? state.pdfPath
      : null
  );
  const sharedPdfDocument = sharedPdfDocumentQuery.data ?? null;

  const replacePdfPreviewImageUrl = useCallback((nextUrl: string | null) => {
    const previousUrl = pdfPreviewImageUrlRef.current;
    pdfPreviewImageUrlRef.current = nextUrl;
    setPdfPreviewImageUrl(nextUrl);

    if (previousUrl && previousUrl !== nextUrl) {
      // Let React commit the new <img src> before retiring the old resource.
      // Immediate revocation is unreliable in iOS WebKit while an image is
      // still decoding, even when JavaScript has already produced the Blob.
      window.setTimeout(() => URL.revokeObjectURL(previousUrl), 1_000);
    }
  }, []);

  useEffect(
    () => () => {
      if (pdfPreviewImageUrlRef.current) {
        URL.revokeObjectURL(pdfPreviewImageUrlRef.current);
      }
    },
    []
  );

  // The PDF preview blob is ONLY consumed inside the panorama dialog
  // (LayoutMapPreview / HotspotDialog). On iOS Safari the WebKit content
  // process has very tight canvas/memory limits, so we wait until the
  // dialog is actually open before rasterising it. Building this eagerly
  // was the main cause of the "shows the PDF, reloads, then crashes"
  // loop on mobile Safari.
  useEffect(() => {
    if (usePdfRasterFallback) {
      replacePdfPreviewImageUrl(null);
      return;
    }
    if (!state.showPhotoCarousel || !state.isPdfLayout || !state.pdfPath) {
      // Drop any preview we generated earlier so its memory can be
      // released as soon as the dialog closes.
      replacePdfPreviewImageUrl(null);
      return;
    }

    // Wait for the same cached document used by the main viewport. Starting
    // a second parse here and then restarting when this query resolves both
    // doubles peak memory and revokes an in-use blob URL on iOS Safari.
    if (!sharedPdfDocument) {
      return;
    }

    let isCancelled = false;

    const buildPdfPreview = async () => {
      try {
        const blob = state.pdfCropRegion
          ? await cropPdfRegionToBlob(
              state.pdfPath!,
              state.pdfPageNumber,
              {
                x: state.pdfCropRegion[0],
                y: state.pdfCropRegion[1],
                width: state.pdfCropRegion[2],
                height: state.pdfCropRegion[3],
              },
              undefined,
              sharedPdfDocument,
              selectedLayoutPdfRotation
            )
          : await renderPdfPageToBlob(
              state.pdfPath!,
              state.pdfPageNumber,
              undefined,
              sharedPdfDocument,
              selectedLayoutPdfRotation
            );

        if (isCancelled) return;
        replacePdfPreviewImageUrl(URL.createObjectURL(blob));
      } catch (error) {
        if (isCancelled) return;
        console.error('Failed to render PDF preview image:', error);
        replacePdfPreviewImageUrl(null);
      }
    };

    buildPdfPreview();

    return () => {
      isCancelled = true;
    };
  }, [
    state.showPhotoCarousel,
    state.isPdfLayout,
    state.pdfPath,
    state.pdfPageNumber,
    state.pdfCropRegion,
    sharedPdfDocument,
    selectedLayoutPdfRotation,
    replacePdfPreviewImageUrl,
    usePdfRasterFallback,
  ]);

  // Apply URL deep-link params once per unique query string.
  useEffect(() => {
    if (!deepLinkParams.key || deepLinkParams.key === lastAppliedDeepLinkRef.current) return;

    // If a layout id was provided, wait until layouts are loaded before applying.
    if (deepLinkParams.layoutId && data.layouts.length === 0) return;

    const resolvedLayoutId = resolveViewerDeepLinkLayoutId(
      deepLinkParams.layoutId,
      data.layouts.map((layout) => layout.id)
    );

    actions.openDeepLinkTarget({
      layoutId: resolvedLayoutId,
      photoId: deepLinkParams.photoId,
      hotspotId: deepLinkParams.hotspotId,
      coordinates: deepLinkParams.coordinates,
    });

    if (deepLinkParams.hotspotId && !deepLinkParams.coordinates) {
      setPendingHotspotId(deepLinkParams.hotspotId);
    } else {
      setPendingHotspotId(null);
    }

    lastAppliedDeepLinkRef.current = deepLinkParams.key;
  }, [deepLinkParams, data.layouts, actions]);

  // Fallback: if hotspot id is provided but pitch/yaw are not, resolve coords from loaded hotspots.
  useEffect(() => {
    if (!pendingHotspotId || !data.hotspotsData?.hotspots) return;

    const hotspot = data.hotspotsData.hotspots.find((h) => h.id === pendingHotspotId);
    if (hotspot) {
      actions.setTargetCoordinates({ pitch: hotspot.pitch - 5, yaw: hotspot.yaw });
      setPendingHotspotId(null);
    }
  }, [pendingHotspotId, data.hotspotsData, actions]);

  const tabs = [
    {
      id: 'layouts',
      label: 'Layouts',
      content: (
        <LayoutsView
          selectedLayoutId={state.selectedLayoutId}
          setSelectedLayoutId={actions.setSelectedLayoutId}
          layouts={data.layouts}
          layoutsLoading={data.layoutsLoading}
          layoutsError={data.layoutsError}
          retryLayouts={() => void data.refetchLayouts()}
          layoutDataLoading={data.layoutDataLoading}
          layoutDataFetching={data.layoutDataFetching}
          isLayoutAssetLoading={data.isLayoutAssetLoading}
          imagePath={state.imagePath}
          pdfPath={state.pdfPath}
          pdfPageNumber={selectedPdfAsset?.pageNumber ?? state.pdfPageNumber}
          pdfRotation={selectedLayoutPdfRotation}
          pdfCropRegion={
            selectedPdfAsset?.cropRegion ? [...selectedPdfAsset.cropRegion] : state.pdfCropRegion
          }
          pdfRasterFallback={pdfRasterFallback}
          flippedHorizontal={selectedTransformedAsset?.flippedHorizontal ?? state.flippedHorizontal}
          flippedVertical={selectedTransformedAsset?.flippedVertical ?? state.flippedVertical}
          isPdfLayout={state.isPdfLayout}
          imageDimensions={state.imageDimensions}
          areaTags={data.areaTags}
          layoutHotspots={data.layoutHotspots}
          projectId={projectId!}
          displayPhotoMarkers={data.displayPhotoMarkers}
          getCursor={actions.getCursor}
          handleMouseDown={interactionHandlers.handleMouseDown}
          handleSvgClick={interactionHandlers.handleSvgClick}
          handleUnitClick={actions.handleUnitClick}
          handleMarkerClick={actions.handleMarkerClick}
          deletePhotoMarker={actions.deletePhotoMarker}
          handleMarkerRightClick={actions.handleMarkerRightClick}
          handleMarkerDragStart={actions.handleMarkerDragStart}
          onWheelZoom={interactionHandlers.handleWheelZoom}
          handleTouchStart={interactionHandlers.handleTouchStart}
          handleTouchMove={interactionHandlers.handleTouchMove}
          handleTouchEnd={interactionHandlers.handleTouchEnd}
          zoom={actions.zoom}
          reset={actions.reset}
          mode={state.mode}
          setMode={actions.handleSetMode}
          photoIndexMode={state.photoIndexMode}
          setCurrentPolygon={actions.setCurrentPolygon}
          closePolygon={actions.closePolygon}
          clearPolygon={actions.clearPolygon}
          savePhotoMarkers={actions.savePhotoMarkers}
          setPhotoIndexMode={actions.setPhotoIndexMode}
          setSelectedMarkers={actions.setSelectedMarkers}
          setShowMarkerToolbar={actions.setShowMarkerToolbar}
          setSelectedUnit={actions.setSelectedUnit}
          setImageDimensions={actions.setImageDimensions}
          completeLayoutAssetLoad={actions.completeLayoutAssetLoad}
          addPhotoMarker={actions.addPhotoMarker}
          svgRef={refs.svgRef}
          groupRef={refs.groupRef}
          position={state.position}
          scale={state.scale}
          setPosition={actions.setPosition}
          setScale={actions.setScale}
          selectedUnit={state.selectedUnit}
          currentPolygon={state.currentPolygon}
          selectedMarkers={state.selectedMarkers}
          showCoordinates={state.showCoordinates}
          savePhotoMarkersMutation={mutations.savePhotoMarkersMutation}
          isHorizontalConstrained={state.isHorizontalConstrained}
          isVerticalConstrained={state.isVerticalConstrained}
        />
      ),
    },
    ...(projectId
      ? [
          {
            id: 'hotspots',
            label: 'Hotspots',
            content: (
              <HotspotsView projectId={projectId!} handleViewHotspot={actions.handleViewHotspot} />
            ),
          },
        ]
      : []),
  ];

  return (
    <BaseLayout fullHeight>
      <InteractiveViewerRoot key={location.pathname} className="h-full bg-background">
        {/* Tabs component that properly handles its own layout */}
        <Tabs tabs={tabs} defaultTab="layouts" />

        {/* Photo Carousel Modal */}
        <Dialog
          modal={false}
          open={state.showPhotoCarousel}
          onOpenChange={handlePanoramaOpenChange}
        >
          <DialogHeader>
            <DialogTitle className="hidden">Photo Carousel</DialogTitle>
            <DialogDescription className="hidden">
              Select a photo to view in the carousel.
            </DialogDescription>
          </DialogHeader>
          <DialogContent
            className="w-screen h-[calc(var(--app-vh,1vh)*100)] max-w-none max-h-[calc(var(--app-vh,1vh)*100)] p-0 rounded-none border-0 md:w-[90vw] md:h-[calc(var(--app-vh,1vh)*90)] md:max-w-[90vw] md:max-h-[calc(var(--app-vh,1vh)*90)] md:rounded-xl"
            closeButtonClassName={PANORAMA_TABLET_DIALOG_CLOSE_CLASS}
            data-tour-id="iv-panorama-dialog"
          >
            {state.openedPhotoId && data.photoData?.image_url ? (
              <Ironview360PanoramaExperience
                photoId={state.openedPhotoId}
                imageUrl={data.photoData.image_url}
                photoData={data.photoData}
                hotspotsData={data.hotspotsData}
                projectId={projectId}
                onNavigateToPhotoId={actions.navigateToMarkerPhotoById}
                onHotspotCreated={actions.refreshHotspots}
                onPanoramaError={actions.refreshPanorama}
                // Share feature - layout context for location preview
                markerNumber={
                  data.displayPhotoMarkers.find((m) =>
                    m.photos.some((p) => p.id === state.openedPhotoId)
                  )?.number
                }
                layoutImageUrl={
                  state.isPdfLayout
                    ? usePdfRasterFallback
                      ? pdfRasterFallback?.url
                      : pdfPreviewImageUrl || undefined
                    : state.imagePath || undefined
                }
                layoutFlippedHorizontal={state.flippedHorizontal}
                layoutFlippedVertical={state.flippedVertical}
                layoutImageDimensions={
                  state.imageDimensions.width && state.imageDimensions.height
                    ? state.imageDimensions
                    : undefined
                }
                photoMarkers={data.displayPhotoMarkers}
                targetCoordinates={state.targetCoordinates}
                onTargetReached={() => actions.setTargetCoordinates(null)}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-white bg-black">
                Loading panorama...
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Area Form Modal */}
        <AreaFormDialog
          open={state.showAreaForm}
          areaName={state.areaName}
          selectedPhotos={state.selectedPhotos}
          availablePhotos={data.availablePhotos}
          onOpenChange={actions.setShowAreaForm}
          onAreaNameChange={actions.setAreaName}
          onSelectedPhotosChange={actions.setSelectedPhotos}
          onSave={actions.saveArea}
          onCancel={() => {
            actions.setShowAreaForm(false);
            actions.setAreaName('');
            actions.setSelectedPhotos([]);
          }}
        />

        {/* Marker Toolbar */}
        <MarkerToolbar
          visible={state.showMarkerToolbar && state.photoIndexMode}
          currentMarkerNumber={state.currentMarkerNumber}
          markerAttributes={state.markerAttributes}
          maintainAspectRatio={state.maintainAspectRatio}
          autoSaveEnabled={state.markerAutoSaveEnabled}
          autoSaveStatus={data.markerAutosave.status}
          autoSaveLastSavedAt={data.markerAutosave.lastSavedAt}
          photoMarkers={data.photoMarkers}
          selectedMarkers={state.selectedMarkers}
          onMarkerNumberChange={actions.setCurrentMarkerNumber}
          onMarkerAttributesChange={actions.setMarkerAttributes}
          onMaintainAspectRatioChange={actions.setMaintainAspectRatio}
          onAutoSaveEnabledChange={actions.setMarkerAutoSaveEnabled}
          onUpdateSelectedMarkers={actions.updateSelectedMarkers}
          onResetSequence={() => {
            // Reset sequence logic would be implemented here
          }}
        />

        {/* Photo Selection Tooltip */}
        <PhotoSelectionTooltip
          tooltip={state.photoSelectionTooltip}
          onPhotoSelect={handlePhotoSelectionTooltipSelect}
          onClose={handlePhotoSelectionTooltipClose}
        />

        {/* Marker Context Menu */}
        <MarkerContextMenu
          visible={state.contextMenu.visible}
          position={state.contextMenu.position}
          selectedCount={state.selectedMarkers.size}
          selectedMarkerId={Array.from(state.selectedMarkers)[0]}
          onDelete={actions.deleteSelectedMarkers}
          onChangeSequence={actions.openSequenceChangeDialog}
          onAdjustSequence={() => actions.setAdjustSequenceMode(true, state.contextMenu.position)}
          onClose={actions.closeContextMenu}
        />

        {/* Change Sequence Dialog */}
        <ChangeSequenceDialog
          open={state.sequenceChangeDialog.visible}
          currentNumber={
            state.sequenceChangeDialog.markerId
              ? data.displayPhotoMarkers.find((m) => m.id === state.sequenceChangeDialog.markerId)
                  ?.number || 1
              : 1
          }
          maxNumber={data.displayPhotoMarkers.length}
          onConfirm={actions.changeMarkerSequence}
          onCancel={actions.closeSequenceChangeDialog}
        />

        {/* Adjust Sequence Widget */}
        <AdjustSequenceWidget
          visible={state.adjustSequenceMode && state.selectedMarkers.size > 0}
          selectedCount={state.selectedMarkers.size}
          position={state.adjustSequencePosition || undefined}
          onIncrement={actions.incrementSelectedMarkers}
          onDecrement={actions.decrementSelectedMarkers}
          onClose={() => actions.setAdjustSequenceMode(false)}
        />
      </InteractiveViewerRoot>
    </BaseLayout>
  );
};

export default InteractiveViewerRefactored;
